/**
 * Country timezone mapping and utility functions
 * Maps ISO country codes to primary IANA timezone (capital/most-populated city)
 * Used by the Best Time to Call tool for timezone calculations
 */

// Map of ISO country codes to primary IANA timezone
export const COUNTRY_TIMEZONES: Record<string, string> = {
  // A
  AD: 'Europe/Andorra',
  AE: 'Asia/Dubai',
  AF: 'Asia/Kabul',
  AG: 'America/Antigua',
  AI: 'America/Anguilla',
  AL: 'Europe/Tirane',
  AM: 'Asia/Yerevan',
  AN: 'America/Curacao',
  AO: 'Africa/Luanda',
  AR: 'America/Argentina/Buenos_Aires',
  AS: 'Pacific/Pago_Pago',
  AT: 'Europe/Vienna',
  AU: 'Australia/Sydney',
  AW: 'America/Aruba',
  AZ: 'Asia/Baku',
  // B
  BA: 'Europe/Sarajevo',
  BB: 'America/Barbados',
  BD: 'Asia/Dhaka',
  BE: 'Europe/Brussels',
  BF: 'Africa/Ouagadougou',
  BG: 'Europe/Sofia',
  BH: 'Asia/Bahrain',
  BI: 'Africa/Bujumbura',
  BJ: 'Africa/Porto-Novo',
  BM: 'Atlantic/Bermuda',
  BN: 'Asia/Brunei',
  BO: 'America/La_Paz',
  BR: 'America/Sao_Paulo',
  BS: 'America/Nassau',
  BT: 'Asia/Thimphu',
  BW: 'Africa/Gaborone',
  BY: 'Europe/Minsk',
  BZ: 'America/Belize',
  // C
  CA: 'America/Toronto',
  CD: 'Africa/Kinshasa',
  CF: 'Africa/Bangui',
  CG: 'Africa/Brazzaville',
  CH: 'Europe/Zurich',
  CI: 'Africa/Abidjan',
  CK: 'Pacific/Rarotonga',
  CL: 'America/Santiago',
  CM: 'Africa/Douala',
  CN: 'Asia/Shanghai',
  CO: 'America/Bogota',
  CR: 'America/Costa_Rica',
  CU: 'America/Havana',
  CV: 'Atlantic/Cape_Verde',
  CY: 'Asia/Nicosia',
  CZ: 'Europe/Prague',
  // D
  DE: 'Europe/Berlin',
  DJ: 'Africa/Djibouti',
  DK: 'Europe/Copenhagen',
  DM: 'America/Dominica',
  DO: 'America/Santo_Domingo',
  DZ: 'Africa/Algiers',
  // E
  EC: 'America/Guayaquil',
  EE: 'Europe/Tallinn',
  EG: 'Africa/Cairo',
  ER: 'Africa/Asmara',
  ES: 'Europe/Madrid',
  ET: 'Africa/Addis_Ababa',
  // F
  FI: 'Europe/Helsinki',
  FJ: 'Pacific/Fiji',
  FM: 'Pacific/Pohnpei',
  FR: 'Europe/Paris',
  // G
  GA: 'Africa/Libreville',
  GB: 'Europe/London',
  GD: 'America/Grenada',
  GE: 'Asia/Tbilisi',
  GF: 'America/Cayenne',
  GH: 'Africa/Accra',
  GI: 'Europe/Gibraltar',
  GL: 'America/Nuuk',
  GM: 'Africa/Banjul',
  GN: 'Africa/Conakry',
  GP: 'America/Guadeloupe',
  GQ: 'Africa/Malabo',
  GR: 'Europe/Athens',
  GT: 'America/Guatemala',
  GU: 'Pacific/Guam',
  GW: 'Africa/Bissau',
  GY: 'America/Guyana',
  // H
  HK: 'Asia/Hong_Kong',
  HN: 'America/Tegucigalpa',
  HR: 'Europe/Zagreb',
  HT: 'America/Port-au-Prince',
  HU: 'Europe/Budapest',
  // I
  ID: 'Asia/Jakarta',
  IE: 'Europe/Dublin',
  IL: 'Asia/Jerusalem',
  IN: 'Asia/Kolkata',
  IQ: 'Asia/Baghdad',
  IR: 'Asia/Tehran',
  IS: 'Atlantic/Reykjavik',
  IT: 'Europe/Rome',
  // J
  JM: 'America/Jamaica',
  JO: 'Asia/Amman',
  JP: 'Asia/Tokyo',
  // K
  KE: 'Africa/Nairobi',
  KG: 'Asia/Bishkek',
  KH: 'Asia/Phnom_Penh',
  KI: 'Pacific/Tarawa',
  KM: 'Indian/Comoro',
  KN: 'America/St_Kitts',
  KP: 'Asia/Pyongyang',
  KR: 'Asia/Seoul',
  KW: 'Asia/Kuwait',
  KY: 'America/Cayman',
  KZ: 'Asia/Almaty',
  // L
  LA: 'Asia/Vientiane',
  LB: 'Asia/Beirut',
  LC: 'America/St_Lucia',
  LI: 'Europe/Vaduz',
  LK: 'Asia/Colombo',
  LR: 'Africa/Monrovia',
  LS: 'Africa/Maseru',
  LT: 'Europe/Vilnius',
  LU: 'Europe/Luxembourg',
  LV: 'Europe/Riga',
  LY: 'Africa/Tripoli',
  // M
  MA: 'Africa/Casablanca',
  MC: 'Europe/Monaco',
  MD: 'Europe/Chisinau',
  ME: 'Europe/Podgorica',
  MG: 'Indian/Antananarivo',
  MH: 'Pacific/Majuro',
  MK: 'Europe/Skopje',
  ML: 'Africa/Bamako',
  MM: 'Asia/Yangon',
  MN: 'Asia/Ulaanbaatar',
  MO: 'Asia/Macau',
  MP: 'Pacific/Saipan',
  MQ: 'America/Martinique',
  MR: 'Africa/Nouakchott',
  MS: 'America/Montserrat',
  MT: 'Europe/Malta',
  MU: 'Indian/Mauritius',
  MV: 'Indian/Maldives',
  MW: 'Africa/Blantyre',
  MX: 'America/Mexico_City',
  MY: 'Asia/Kuala_Lumpur',
  MZ: 'Africa/Maputo',
  // N
  NA: 'Africa/Windhoek',
  NC: 'Pacific/Noumea',
  NE: 'Africa/Niamey',
  NF: 'Pacific/Norfolk',
  NG: 'Africa/Lagos',
  NI: 'America/Managua',
  NL: 'Europe/Amsterdam',
  NO: 'Europe/Oslo',
  NP: 'Asia/Kathmandu',
  NR: 'Pacific/Nauru',
  NU: 'Pacific/Niue',
  NZ: 'Pacific/Auckland',
  // O
  OM: 'Asia/Muscat',
  // P
  PA: 'America/Panama',
  PE: 'America/Lima',
  PF: 'Pacific/Tahiti',
  PG: 'Pacific/Port_Moresby',
  PH: 'Asia/Manila',
  PK: 'Asia/Karachi',
  PL: 'Europe/Warsaw',
  PM: 'America/Miquelon',
  PN: 'Pacific/Pitcairn',
  PR: 'America/Puerto_Rico',
  PT: 'Europe/Lisbon',
  PW: 'Pacific/Palau',
  PY: 'America/Asuncion',
  // Q
  QA: 'Asia/Qatar',
  // R
  RE: 'Indian/Reunion',
  RO: 'Europe/Bucharest',
  RS: 'Europe/Belgrade',
  RU: 'Europe/Moscow',
  RW: 'Africa/Kigali',
  // S
  SA: 'Asia/Riyadh',
  SB: 'Pacific/Guadalcanal',
  SC: 'Indian/Mahe',
  SD: 'Africa/Khartoum',
  SE: 'Europe/Stockholm',
  SG: 'Asia/Singapore',
  SH: 'Atlantic/St_Helena',
  SI: 'Europe/Ljubljana',
  SJ: 'Arctic/Longyearbyen',
  SK: 'Europe/Bratislava',
  SL: 'Africa/Freetown',
  SM: 'Europe/San_Marino',
  SN: 'Africa/Dakar',
  SO: 'Africa/Mogadishu',
  SR: 'America/Paramaribo',
  SS: 'Africa/Juba',
  ST: 'Africa/Sao_Tome',
  SV: 'America/El_Salvador',
  SY: 'Asia/Damascus',
  SZ: 'Africa/Mbabane',
  // T
  TC: 'America/Grand_Turk',
  TD: 'Africa/Ndjamena',
  TG: 'Africa/Lome',
  TH: 'Asia/Bangkok',
  TJ: 'Asia/Dushanbe',
  TK: 'Pacific/Fakaofo',
  TL: 'Asia/Dili',
  TM: 'Asia/Ashgabat',
  TN: 'Africa/Tunis',
  TO: 'Pacific/Tongatapu',
  TR: 'Europe/Istanbul',
  TT: 'America/Port_of_Spain',
  TV: 'Pacific/Funafuti',
  TW: 'Asia/Taipei',
  TZ: 'Africa/Dar_es_Salaam',
  // U
  UA: 'Europe/Kiev',
  UG: 'Africa/Kampala',
  US: 'America/New_York',
  UY: 'America/Montevideo',
  UZ: 'Asia/Tashkent',
  // V
  VA: 'Europe/Vatican',
  VC: 'America/St_Vincent',
  VE: 'America/Caracas',
  VG: 'America/Tortola',
  VI: 'America/St_Thomas',
  VN: 'Asia/Ho_Chi_Minh',
  VU: 'Pacific/Efate',
  // W
  WF: 'Pacific/Wallis',
  WS: 'Pacific/Apia',
  // Y
  YE: 'Asia/Aden',
  YT: 'Indian/Mayotte',
  // Z
  ZA: 'Africa/Johannesburg',
  ZM: 'Africa/Lusaka',
  ZW: 'Africa/Harare',
}

// Build reverse lookup: timezone → country code
const TIMEZONE_TO_COUNTRY: Record<string, string> = Object.entries(COUNTRY_TIMEZONES).reduce(
  (acc, [code, tz]) => {
    acc[tz] = code
    return acc
  },
  {} as Record<string, string>
)

/**
 * Get the primary IANA timezone for a country
 */
export function getCountryTimezone(code: string): string | undefined {
  return COUNTRY_TIMEZONES[code.toUpperCase()]
}

/**
 * Detect the user's country code based on their browser timezone
 */
export function detectUserCountry(): string | null {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return TIMEZONE_TO_COUNTRY[timeZone] || null
  } catch {
    return null
  }
}

interface TimeResult {
  hours: number // 0-23
  minutes: number // 0-59
  formatted: string // e.g. "2:30 PM"
  date: Date
}

/**
 * Get the current local time in a specific country
 */
export function getCurrentTimeInCountry(code: string): TimeResult | null {
  const timezone = getCountryTimezone(code)
  if (!timezone) return null

  const now = new Date()

  try {
    // Get formatted time string with AM/PM
    const timeFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    })

    const formatted = timeFormatter.format(now)

    // Extract hours (0-23) using 24h format for calculations
    const hour24Formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      hour12: false,
    })

    // Safely get hour and minute using string parsing approach
    const timeString24 = hour24Formatter.format(now)
    const parts24 = timeString24.match(/(\d+)/)
    const hours = parts24 ? parseInt(parts24[1], 10) : now.getHours()

    // Get minutes
    const minuteFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      minute: 'numeric',
    })
    const minuteString = minuteFormatter.format(now)
    const minuteParts = minuteString.match(/(\d+)/)
    const minutes = minuteParts ? parseInt(minuteParts[1], 10) : now.getMinutes()

    return {
      hours,
      minutes,
      formatted,
      date: now,
    }
  } catch (error) {
    console.error('Error getting time for country:', code, error)
    return null
  }
}

/**
 * Get the UTC offset for a timezone (in hours, DST-aware)
 * Returns offset from UTC (e.g., -5 for EST, +5.5 for India)
 */
export function getUTCOffset(timezone: string): number {
  try {
    const now = new Date()

    // Create a formatter for the target timezone
    const targetFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: false,
    })

    // Get target timezone parts
    const targetParts = targetFormatter.formatToParts(now)
    const targetDate = new Date(
      parseInt(targetParts.find((p) => p.type === 'year')?.value || '0'),
      parseInt(targetParts.find((p) => p.type === 'month')?.value || '1') - 1,
      parseInt(targetParts.find((p) => p.type === 'day')?.value || '1'),
      parseInt(targetParts.find((p) => p.type === 'hour')?.value || '0'),
      parseInt(targetParts.find((p) => p.type === 'minute')?.value || '0'),
      parseInt(targetParts.find((p) => p.type === 'second')?.value || '0')
    )

    // Calculate offset in hours
    return (targetDate.getTime() - now.getTime()) / (1000 * 60 * 60)
  } catch {
    return 0
  }
}

/**
 * Get the time difference between two countries (in hours)
 * Positive = target is ahead, Negative = target is behind
 */
export function getTimeDifference(fromCode: string, toCode: string): number {
  const fromTimezone = getCountryTimezone(fromCode)
  const toTimezone = getCountryTimezone(toCode)

  if (!fromTimezone || !toTimezone) return 0

  const fromOffset = getUTCOffset(fromTimezone)
  const toOffset = getUTCOffset(toTimezone)

  return toOffset - fromOffset
}

export type HourQuality = 'great' | 'okay' | 'avoid'

interface HourInfo {
  hour: number // 0-23
  fromQuality: HourQuality
  toQuality: HourQuality
  overallQuality: HourQuality
}

interface BestCallingHoursResult {
  hours: HourInfo[]
  bestStartHour: number | null
  bestEndHour: number | null
  overlapHours: number
  recommendation: string
}

/**
 * Calculate hour-by-hour quality ratings for calling between two countries
 * Business hours: 9AM-9PM (21:00)
 * Great: Both in business hours
 * Okay: One in business hours, one in 7-9AM or 9-11PM
 * Avoid: Either before 7AM or after 11PM
 */
export function getBestCallingHours(
  fromCode: string,
  toCode: string,
  fromStartHour = 9,
  fromEndHour = 21,
  toStartHour = 9,
  toEndHour = 21
): BestCallingHoursResult {
  const fromTimezone = getCountryTimezone(fromCode)
  const toTimezone = getCountryTimezone(toCode)

  if (!fromTimezone || !toTimezone) {
    return {
      hours: [],
      bestStartHour: null,
      bestEndHour: null,
      overlapHours: 0,
      recommendation: '',
    }
  }

  const timeDiff = getTimeDifference(fromCode, toCode)
  const hours: HourInfo[] = []
  let bestStartHour: number | null = null
  let bestEndHour: number | null = null
  let currentBestStart: number | null = null
  let maxOverlapHours = 0
  let currentOverlapHours = 0

  for (let fromHour = 0; fromHour < 24; fromHour++) {
    // Calculate corresponding hour in target country
    let toHour = Math.round(fromHour + timeDiff) % 24
    if (toHour < 0) toHour += 24

    // Determine quality for each country
    const getQuality = (hour: number, start: number, end: number): HourQuality => {
      if (hour >= start && hour < end) return 'great'
      if ((hour >= 7 && hour < start) || (hour >= end && hour < 23)) return 'okay'
      return 'avoid'
    }

    const fromQuality = getQuality(fromHour, fromStartHour, fromEndHour)
    const toQuality = getQuality(toHour, toStartHour, toEndHour)

    // Overall quality is the worst of the two
    let overallQuality: HourQuality = 'great'
    if (fromQuality === 'avoid' || toQuality === 'avoid') {
      overallQuality = 'avoid'
    } else if (fromQuality === 'okay' || toQuality === 'okay') {
      overallQuality = 'okay'
    }

    hours.push({ hour: fromHour, fromQuality, toQuality, overallQuality })

    // Track best consecutive hours
    if (overallQuality === 'great') {
      if (currentBestStart === null) {
        currentBestStart = fromHour
      }
      currentOverlapHours++
    } else {
      if (currentBestStart !== null && currentOverlapHours > maxOverlapHours) {
        bestStartHour = currentBestStart
        bestEndHour = fromHour
        maxOverlapHours = currentOverlapHours
      }
      currentBestStart = null
      currentOverlapHours = 0
    }
  }

  // Check if the best window wraps around midnight
  if (currentBestStart !== null && currentOverlapHours > maxOverlapHours) {
    bestStartHour = currentBestStart
    bestEndHour = 24
    maxOverlapHours = currentOverlapHours
  }

  // Generate recommendation text
  let recommendation = ''
  if (bestStartHour !== null && bestEndHour !== null) {
    const startTime = formatHour(bestStartHour)
    const endTime = formatHour(bestEndHour)
    recommendation = `${startTime} - ${endTime}`
  }

  return {
    hours,
    bestStartHour,
    bestEndHour,
    overlapHours: maxOverlapHours,
    recommendation,
  }
}

/**
 * Format hour number to readable string
 */
function formatHour(hour: number): string {
  const period = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
  return `${displayHour} ${period}`
}

/**
 * Check if the current time is a good time to call
 */
export function isGoodCallingTime(fromCode: string, toCode: string): boolean {
  const fromTime = getCurrentTimeInCountry(fromCode)

  if (!fromTime) return false

  const result = getBestCallingHours(fromCode, toCode)
  const currentHourInfo = result.hours[fromTime.hours]

  return currentHourInfo?.overallQuality === 'great'
}
